/** base64url helpers + WebAuthn option/result mapping for the passkey endpoints (snake_case wire). */

export function b64urlToBuf(s: string): ArrayBuffer {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

export function bufToB64url(buf: ArrayBuffer | null): string {
  if (!buf) return '';
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;
}

type Descriptor = { id: string; type?: string; transports?: string[] };

export interface RegisterOptionsWire {
  challenge: string;
  rp_id?: string;
  rp_name?: string;
  user_id: string;
  user_name: string;
  user_display_name?: string;
  timeout?: number;
  exclude_credentials?: Descriptor[];
  pub_key_cred_params?: { type: 'public-key'; alg: number }[];
  authenticator_selection?: AuthenticatorSelectionCriteria;
  attestation?: AttestationConveyancePreference;
}

export interface LoginOptionsWire {
  challenge: string;
  rp_id?: string;
  timeout?: number;
  allow_credentials?: Descriptor[];
  user_verification?: UserVerificationRequirement;
}

export function toCreationOptions(o: RegisterOptionsWire, rpIdOverride?: string): PublicKeyCredentialCreationOptions {
  return {
    challenge: b64urlToBuf(o.challenge),
    rp: { id: o.rp_id ?? rpIdOverride, name: o.rp_name ?? 'Facturita' },
    user: {
      id: b64urlToBuf(o.user_id),
      name: o.user_name,
      displayName: o.user_display_name ?? o.user_name,
    },
    pubKeyCredParams: o.pub_key_cred_params ?? [
      { type: 'public-key', alg: -7 },
      { type: 'public-key', alg: -257 },
    ],
    timeout: o.timeout ?? 60_000,
    excludeCredentials: (o.exclude_credentials ?? []).map((d) => ({
      id: b64urlToBuf(d.id),
      type: 'public-key',
      transports: d.transports as AuthenticatorTransport[] | undefined,
    })),
    authenticatorSelection: o.authenticator_selection ?? { residentKey: 'preferred', userVerification: 'preferred' },
    attestation: o.attestation ?? 'none',
  };
}

export function toRequestOptions(o: LoginOptionsWire, rpIdOverride?: string): PublicKeyCredentialRequestOptions {
  return {
    challenge: b64urlToBuf(o.challenge),
    rpId: o.rp_id ?? rpIdOverride,
    timeout: o.timeout ?? 60_000,
    allowCredentials: (o.allow_credentials ?? []).map((d) => ({
      id: b64urlToBuf(d.id),
      type: 'public-key',
      transports: d.transports as AuthenticatorTransport[] | undefined,
    })),
    userVerification: o.user_verification ?? 'preferred',
  };
}

export function serializeAttestation(cred: PublicKeyCredential) {
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    credential_id: cred.id,
    raw_id: bufToB64url(cred.rawId),
    response: {
      attestation_object: bufToB64url(r.attestationObject),
      client_data_json: bufToB64url(r.clientDataJSON),
      transports: typeof r.getTransports === 'function' ? r.getTransports() : [],
    },
  };
}

export function serializeAssertion(cred: PublicKeyCredential) {
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    credential_id: cred.id,
    raw_id: bufToB64url(cred.rawId),
    response: {
      authenticator_data: bufToB64url(r.authenticatorData),
      client_data_json: bufToB64url(r.clientDataJSON),
      signature: bufToB64url(r.signature),
      user_handle: bufToB64url(r.userHandle),
    },
  };
}

export function deviceName(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : /Firefox\//.test(ua) ? 'Firefox' : 'Navegador';
  const os = /Mac/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : '';
  return `${browser}${os ? ` · ${os}` : ''} (web)`;
}
